/*
  The visitor's PCG office: the one they chose, the ones their time zone suggests, and
  where "Submit cards" should go (docs/specs/pop-language-split.md D20-D23).

  The footer and the AppBar both read this. Only `chooseOffice` writes, and it is only ever
  called from the visitor's own choice in the footer (D22). It saves the office and tells
  every mounted copy of this hook, so the header's Submit cards follows a choice made in
  the footer of the same page without a reload. Choices made in another tab arrive through
  the browser's own `storage` event.

  The suggestion is read once: the browser's clock does not change zone under an open page.

  Lives here beside useLanguageBucket rather than in lib/, whose modules touch no React.
*/

import { useEffect, useMemo, useState } from "react";

import {
  OFFICE_STORAGE_KEY,
  Office,
  detectTimeZone,
  readSavedOffice,
  saveOffice,
  submitHref,
  suggestOffices,
} from "lib/offices";

export const OFFICE_CHANGE_EVENT = "pcg-office-change";

export const chooseOffice = (office: Office): void => {
  saveOffice(office);
  window.dispatchEvent(new CustomEvent<Office>(OFFICE_CHANGE_EVENT, { detail: office }));
};

export interface OfficeChoice {
  saved: Office | null;
  suggested: Office[];
  /* D23: where the header's Submit cards goes. */
  submitHref: string;
}

export const useOfficeChoice = (): OfficeChoice => {
  const [saved, setSaved] = useState<Office | null>(() => readSavedOffice());
  const suggested = useMemo(() => suggestOffices(detectTimeZone()), []);

  useEffect(() => {
    /*
      Read the office from the event rather than storage: with storage blocked the save
      fails, and the choice should still hold for this page.
    */
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<Office>).detail;
      setSaved(detail ?? readSavedOffice());
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === OFFICE_STORAGE_KEY) {
        setSaved(readSavedOffice());
      }
    };
    window.addEventListener(OFFICE_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(OFFICE_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const href = useMemo(() => submitHref(saved, suggested), [saved, suggested]);

  return { saved, suggested, submitHref: href };
};
